import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  Button,
  Snackbar,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  CircularProgress
  , Tabs, Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tooltip,
  Fade,
  Zoom
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Description as DocumentIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { FiberManualRecord as DotIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import { useNavigate } from 'react-router-dom';

// Project import
import { gridSpacing } from 'config.js';
import { employeesData } from '../../Data/Kontak/Employee';
import StatusConfirmationModal from 'component/StatusConfirmationModal';

const EmployeePage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusModal, setStatusModal] = useState({ open: false, data: null });
  // Only two statuses shown in UI
  const activeStatuses = ['Active', 'Inactive'];
  const [offices, setOffices] = useState([]); // State to store office data
  // tab: 'Active' or 'Inactive'
  const [tab, setTab] = useState('Active');
  const [selectedOffice, setSelectedOffice] = useState(''); // State for selected office
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedBU, setSelectedBU] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successPopup, setSuccessPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [documentModal, setDocumentModal] = useState({ open: false, employee: null });
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [tokenRemaining, setTokenRemaining] = useState('');

  const [customUploadModal, setCustomUploadModal] = useState({ open: false, files: [{ filename: '', file: null }], employee: null, uploading: false });

  // Update token remaining time for document modal (updates every 30s while modal is open)
  useEffect(() => {
    if (!documentModal.open || !documentModal.employee?.tokenExpiry) {
      setTokenRemaining('');
      return;
    }

    const updateRemaining = () => {
      try {
        const expiry = new Date(documentModal.employee.tokenExpiry).getTime();
        const diff = expiry - Date.now();
        if (diff <= 0) {
          setTokenRemaining('Sudah kadaluwarsa');
          return;
        }
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTokenRemaining(`${hours} jam ${minutes} menit`);
      } catch (e) {
        setTokenRemaining('menghitung...');
      }
    };

    updateRemaining();
    const iv = setInterval(updateRemaining, 30000);
    return () => clearInterval(iv);
  }, [documentModal.open, documentModal.employee?.tokenExpiry]);


  // Initial data loader effect (was missing opening useEffect)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // NOTE: ngrok free tunnels inject a browser warning page unless the
        // 'ngrok-skip-browser-warning' header is sent. Without it you'll get an
        // HTML document (200 OK) instead of your JSON API response.
        // We also explicitly request JSON and include Authorization if present.
        const ngrokUrl = 'https://apidashboard.eurekagroup.id/employee';
        const token = localStorage.getItem('token');
        const headers = {
          'ngrok-skip-browser-warning': 'true',
          'Accept': 'application/json'
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(ngrokUrl, { headers });
        const result = await response.json();

        // Convert static data to match API format
        const staticDataConverted = employeesData.map(emp => ({
          _id: `static_${emp.id}`,
          emp_id: emp.personalData.employee_code,
          full_name: emp.personalData.employee_name,
          sex: emp.personalData.gender === 'L' ? 'Male' : 'Female',
          email: emp.contact.corporate_email,
          no_telp: emp.contact.mobile_phone,
          is_active: emp.personalData.active_status === 1 ? 'Active' : 'Inactive',
          birth_date: emp.personalData.birth_date,
          address: emp.address.street,
          // Add static data fields
          staticData: {
            personalData: emp.personalData,
            workInfo: emp.workInfo,
            address: emp.address,
            benefit: emp.benefit,
            contact: emp.contact,
            tax: emp.tax,
            others: emp.others
          },
          isStatic: true
        }));

        if (Array.isArray(result)) {
          // Use document_count provided by backend (do NOT call /documents/list-gcs here)
          const employeesNoGcs = result.map(emp => ({ ...emp, document_count: emp.document_count || 0, isStatic: false }));
          // Combine dynamic and static data
          const combinedData = [...employeesNoGcs, ...staticDataConverted];
          setData(combinedData);
        } else {
          console.error('Unexpected data format:', result);
          setData(staticDataConverted); // Fallback to static data only
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
        // Fallback to static data only
        const staticDataConverted = employeesData.map(emp => ({
          _id: `static_${emp.id}`,
          emp_id: emp.personalData.employee_code,
          full_name: emp.personalData.employee_name,
          sex: emp.personalData.gender === 'L' ? 'Male' : 'Female',
          email: emp.contact.corporate_email,
          no_telp: emp.contact.mobile_phone,
          is_active: emp.personalData.active_status === 1 ? 'Active' : 'Inactive',
          staticData: {
            personalData: emp.personalData,
            workInfo: emp.workInfo,
            address: emp.address,
            benefit: emp.benefit,
            contact: emp.contact,
            tax: emp.tax,
            others: emp.others
          },
          isStatic: true
        }));
        setData(staticDataConverted);
      }
    };

    // const fetchActiveStatus = async () => {
    //   try {
    //     const response = await fetch('https://apidashboard.eurekagroup.id/employee/get/is_active');
    //     const result = await response.json();
    //     if (Array.isArray(result)) {
    //       setActiveStatuses(result);
    //     } else {
    //       console.error('Unexpected active status format:', result);
    //       setActiveStatuses([]);
    //     }
    //   } catch (error) {
    //     console.error('Error fetching active statuses:', error);
    //     setActiveStatuses([]);
    //   }
    // };

    const fetchBusinessUnits = async () => {
      try {
        const buResponse = await fetch('https://apidashboard.eurekagroup.id/bu'); // Adjust API URL as needed
        const buResult = await buResponse.json();
        if (Array.isArray(buResult)) {
          setBusinessUnits(buResult);
        } else {
          console.error('Unexpected business units format:', buResult);
          setBusinessUnits([]);
        }
      } catch (error) {
        console.error('Error fetching business units:', error);
        setBusinessUnits([]);
      }
    };

    const fetchOffices = async () => { // New function to fetch office data
      try {
        const officeResponse = await fetch('https://apidashboard.eurekagroup.id/office');
        const officeResult = await officeResponse.json();
        if (Array.isArray(officeResult)) {
          setOffices(officeResult);
        } else {
          console.error('Unexpected office format:', officeResult);
          setOffices([]);
        }
      } catch (error) {
        console.error('Error fetching offices:', error);
        setOffices([]);
      }
    };

    const loadData = async () => {
      await Promise.all([
        fetchData(),
        fetchBusinessUnits(),
        fetchOffices()
      ]);
      setLoading(false);
    };

    loadData();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleBUChange = (event) => {
    setSelectedBU(event.target.value);
  };

  const handleOfficeChange = (event) => { // New handler for office dropdown
    setSelectedOffice(event.target.value);
  };

  const handleActiveStatusChange = (event) => { // New handler for active status dropdown
    // kept for backward compatibility if needed but not used; prefer tab state
    // no-op: use tabs instead
    return;
  };

  const handleOpenCustomUploadModal = (employee) => {
    setCustomUploadModal({ open: true, files: [{ filename: '', file: null }], employee, uploading: false });
  };
  const handleCloseCustomUploadModal = () => {
    setCustomUploadModal({ open: false, files: [{ filename: '', file: null }], employee: null, uploading: false });
  };
  const handleAddCustomFileField = () => {
    setCustomUploadModal((prev) => ({ ...prev, files: [...prev.files, { filename: '', file: null }] }));
  };
  const handleCustomFileChange = (idx, type, value) => {
    setCustomUploadModal((prev) => {
      const files = prev.files.map((f, i) => i === idx ? { ...f, [type]: value } : f);
      return { ...prev, files };
    });
  };
  const handleCustomUpload = async () => {
    if (!customUploadModal.employee) return;
    setCustomUploadModal((prev) => ({ ...prev, uploading: true }));
    const emp = customUploadModal.employee;
    let successCount = 0;
    for (const f of customUploadModal.files) {
      if (!f.file || !f.filename) continue;
      const originalExt = f.file.name.includes('.') ? f.file.name.split('.').pop() : (f.file.type === 'application/pdf' ? 'pdf' : 'pdf');
      const backendFileName = `${f.filename}.${originalExt}`;
      const folder = `${emp.full_name || 'Unknown'}-${emp.emp_id || 'Unknown'}`;
      const gsUri = `gs://hris-eureka-documents/${folder}/${backendFileName}`;
      const formData = new FormData();
      formData.append('file', f.file);
      formData.append('nama', emp.full_name || '');
      formData.append('nim', emp.emp_id || '');
      formData.append('document_type', f.filename);
      formData.append('filename', backendFileName);
      formData.append('folder', folder);
      formData.append('gsUri', gsUri);
      formData.append('id_user', emp._id);
      try {
        const response = await fetch('https://apidashboard.eurekagroup.id/documents/upload', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
        const json = await response.json();
        if (response.ok && json && json.success === true) {
          successCount++;
        }
      } catch (err) { }
    }
    try {
      const empResponse = await fetch(`https://apidashboard.eurekagroup.id/employee/get/${emp._id}`);
      const updatedEmployee = await empResponse.json();
      setData(prevData => prevData.map(e => e._id === emp._id ? { ...e, ...updatedEmployee } : e));
    } catch { }
    setCustomUploadModal({ open: false, files: [{ filename: '', file: null }], employee: null, uploading: false });
    Swal.fire({
      icon: successCount > 0 ? 'success' : 'error',
      title: successCount > 0 ? 'Upload Berhasil!' : 'Upload Gagal',
      text: successCount > 0 ? `${successCount} dokumen berhasil diupload.` : 'Tidak ada dokumen yang berhasil diupload.',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusClick = (id, newStatus) => {
    const employee = data.find(e => e._id === id);
    if (!employee) return;

    setStatusModal({
      open: true,
      data: {
        id,
        newStatus,
        currentStatus: employee.is_active,
        employee
      }
    });
  };

  const handleStatusConfirm = async () => {
    const { data: modalData } = statusModal;
    const { id, newStatus, employee } = modalData;

    // Pastikan id ada
    if (!id) {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Employee ID is missing!'
      });
      return;
    }

    // Gunakan FormData
    const formData = new FormData();
    formData.append('id', id);               // <--- ID wajib ada di body
    // Use 'is_active' as the payload field so backend recognizes active flag
    formData.append('is_active', newStatus);

    // Jika ada field lain yang ingin tetap dikirim (misal foto, dll), tambahkan:
    // Object.keys(employee).forEach(key => {
    //   if (key !== 'status') formData.append(key, employee[key]);
    // });

    try {
      const response = await fetch(`https://apidashboard.eurekagroup.id/employee/edit`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
          // JANGAN set 'Content-Type' → browser otomatis set multipart/form-data + boundary
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Update UI (use is_active field in the UI data)
      setData(prevData =>
        prevData.map(e =>
          e._id === id ? { ...e, is_active: newStatus } : e
        )
      );

      SwalConfig.success(
        'Status Updated!',
        `Employee status has been updated to ${newStatus}`,
        2000
      );
    } catch (error) {
      console.error('Error updating employee status:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Failed to update status: ${error.message}`
      });
    } finally {
      setStatusModal({ open: false, data: null });
    }
  };

  const handleDeleteClick = (id) => {
    SwalConfig.deleteConfirm('karyawan ini').then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem('token'); // Get token from localStorage

        try {
          const response = await fetch(`https://apidashboard.eurekagroup.id/employee/delete/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`, // Add Authorization header with Bearer token
            },
          });

          if (response.ok) {
            // Filter out the deleted employee from the data array
            setData((prevData) => prevData.filter(employee => employee._id !== id));

            SwalConfig.success(
              'Berhasil Dihapus!',
              'Data karyawan telah berhasil dihapus dari sistem.',
              2000
            );
          } else {
            throw new Error('Gagal menghapus data');
          }
        } catch (error) {
          SwalConfig.error('Error!', 'Terjadi kesalahan saat menghapus data.');
        }
      }
    });
  };

  // Helper: confirm and update multiple employees' is_active field
  const confirmBulkIsActiveChange = async (targetStatus, ids = []) => {
    if (!ids || ids.length === 0) return;

    const result = await Swal.fire({
      title: `Ubah status ${ids.length} karyawan menjadi ${targetStatus}?`,
      text: 'Tindakan ini akan mengubah status aktif mereka.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, ubah',
      cancelButtonText: 'Batal'
    });

    if (!result.isConfirmed) return;

    const token = localStorage.getItem('token');
    const failures = [];

    for (const id of ids) {
      try {
        const fd = new FormData();
        fd.append('id', id);
        fd.append('is_active', targetStatus);

        const res = await fetch(`https://apidashboard.eurekagroup.id/employee/edit`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: fd
        });

        if (!res.ok) {
          failures.push(id);
        } else {
          // update local UI
          setData(prev => prev.map(e => e._id === id ? { ...e, is_active: targetStatus } : e));
        }
      } catch (err) {
        failures.push(id);
      }
    }

    if (failures.length === 0) {
      SwalConfig.success('Berhasil', `${ids.length} karyawan berhasil diperbarui`, 2000);
    } else {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Sebagian gagal',
        text: `Gagal memperbarui ${failures.length} dari ${ids.length} karyawan.`
      });
    }
  };

  // Generate unique token from frontend with 72 hours expiry
  const generateUniqueToken = (employeeId) => {
    const timestamp = new Date().getTime();
    const random = Math.random().toString(36).substring(2, 15);
    const token = `${employeeId}_${timestamp}_${random}`;
    const expiry = new Date(timestamp + 72 * 60 * 60 * 1000); // 72 hours (3 days) from now

    // Store in localStorage for validation
    const tokenData = {
      token,
      employeeId,
      expiresAt: expiry.toISOString()
    };
    localStorage.setItem(`public_token_${token}`, JSON.stringify(tokenData));

    return { token, expiresAt: expiry };
  };

  // Document Modal Handlers
  const handleDocumentClick = async (employee) => {
    try {
      // Selalu ambil data employee terbaru sebelum buka modal dokumen
      const response = await fetch(`https://apidashboard.eurekagroup.id/employee/get/${employee._id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch latest employee data');
      }

      const latestEmployee = await response.json();

      // Add document_count and also attempt to fetch GCS listing so admin modal
      // can reflect files that exist in storage even if backend didn't create records.
      latestEmployee.document_count = latestEmployee.document_count || (latestEmployee.documents ? latestEmployee.documents.length : 0);

      // Try to fetch GCS list for this employee (POST { emp_id, full_name })
      try {
        const gcsResp = await fetch('https://apidashboard.eurekagroup.id/documents/list-gcs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emp_id: latestEmployee.emp_id || '', full_name: latestEmployee.full_name || '' })
        });
        if (gcsResp.ok) {
          const gcsJson = await gcsResp.json();
          const items = Array.isArray(gcsJson.items) ? gcsJson.items : (Array.isArray(gcsJson) ? gcsJson : []);
          // dedupe by filename (case-insensitive)
          const seen = new Set();
          const unique = [];
          items.forEach(it => {
            const filename = (it && (it.filename || it.name || it.document_name) || '').toString().trim();
            const key = filename.toLowerCase();
            if (filename && !seen.has(key)) {
              seen.add(key);
              unique.push(it);
            }
          });
          latestEmployee.gcsItems = unique;
        } else {
          latestEmployee.gcsItems = [];
        }
      } catch (err) {
        console.error('Error fetching list-gcs for admin modal:', err);
        latestEmployee.gcsItems = [];
      }

      // Generate public token berbasis _id terbaru (harus sama)
      const { token, expiresAt } = generateUniqueToken(latestEmployee._id);

      // Update list utama juga supaya sinkron (preserve computed document_count)
      setData(prevData =>
        prevData.map(e =>
          e._id === latestEmployee._id ? { ...e, ...latestEmployee } : e
        )
      );

      setDocumentModal({
        open: true,
        employee: { ...latestEmployee, publicToken: token, tokenExpiry: expiresAt }
      });
      // Note: removed runtime list-gcs checks; modal will use employee.documents only
    } catch (error) {
      console.error('Error fetching latest employee data:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Gagal memuat data karyawan terbaru. Coba lagi.'
      });
    }
  };

  const handleCloseDocumentModal = () => {
    setDocumentModal({ open: false, employee: null });
    setUploadingDoc(null);
  };

  const handleFileUpload = async (documentName, file) => {
    if (!file || !documentModal.employee) return;

    setUploadingDoc(documentName);

    // Ekstensi dari file asli (default pdf)
    const originalExt = file.name.includes('.')
      ? file.name.split('.').pop()
      : (file.type === 'application/pdf' ? 'pdf' : 'pdf');

    // Bersihkan nama dokumen (hapus teks dalam kurung, rapikan spasi)
    const sanitizedLabel = documentName
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    const backendFileName = `${sanitizedLabel}.${originalExt}`;

    // Folder: <FullName>-<EmpId>
    const emp = documentModal.employee;
    const folder = `${emp.full_name || 'Unknown'}-${emp.emp_id || 'Unknown'}`;

    const gsUri = `gs://hris-eureka-documents/${folder}/${backendFileName}`;

    // Samakan payload dengan PublicEmployeeDocuments: FormData field
    const formData = new FormData();

    // file (binary)
    formData.append('file', file);
    // nama: nama lengkap employee
    formData.append('nama', emp.full_name || '');
    // nim: emp_id karyawan
    formData.append('nim', emp.emp_id || '');
    // document_type: label dokumen sudah dibersihkan
    formData.append('document_type', sanitizedLabel);
    // filename/folder/gsUri: metadata path
    formData.append('filename', backendFileName);
    formData.append('folder', folder);
    formData.append('gsUri', gsUri);
    // id_user: id internal employee
    formData.append('id_user', emp._id);

    try {
      const response = await fetch('https://apidashboard.eurekagroup.id/documents/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
          // JANGAN set Content-Type, biar browser yang set multipart boundary
        },
        body: formData
      });

      let json = null;
      try {
        json = await response.json();
      } catch (e) {
        json = null;
      }

      const isSuccess = response.ok && json && json.success === true;

      if (!isSuccess) {
        let msg = 'Gagal mengunggah dokumen';

        if (json) {
          if (json.message) {
            msg = json.message;
          } else if (json.error) {
            msg = json.error; // contoh: {"error":"Only PDF or JPG/JPEG allowed"}
          } else if (typeof json === 'string') {
            msg = json;
          }
        }

        if (json?.error === 'Only PDF or JPG/JPEG allowed') {
          msg = 'Tipe file tidak diperbolehkan. Hanya boleh PDF atau JPG/JPEG.';
        }

        throw new Error(msg);
      }

      // Pastikan modal SweetAlert selalu di atas modal MUI
      Swal.fire({
        icon: 'success',
        title: 'Upload Berhasil!',
        html: `
          <div style="text-align:left;font-size:0.9rem;">
            <p><strong>Dokumen:</strong> ${json.filename || backendFileName}</p>
            <p><strong>Folder:</strong> ${json.folder || folder}</p>
            <p><strong>Lokasi:</strong><br/><code style="font-size:0.75rem;">${json.gsUri || gsUri}</code></p>
          </div>
        `,
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        heightAuto: false,
        backdrop: 'rgba(0,0,0,0.6)',
        didOpen: () => {
          const popup = Swal.getPopup();
          if (popup) {
            popup.style.zIndex = '99999';
          }
          const container = Swal.getContainer();
          if (container) {
            container.style.zIndex = '99998';
          }
        }
      });

      // Refresh employee data setelah upload
      const empResponse = await fetch(`https://apidashboard.eurekagroup.id/employee/get/${emp._id}`);
      const updatedEmployee = await empResponse.json();

      setData(prevData =>
        prevData.map(e =>
          e._id === emp._id ? { ...e, ...updatedEmployee } : e
        )
      );

      setDocumentModal(prev => ({ ...prev, employee: { ...updatedEmployee, publicToken: prev.employee.publicToken, tokenExpiry: prev.employee.tokenExpiry } }));
    } catch (error) {
      console.error('Error uploading document:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Upload Gagal',
        text: error.message || 'Gagal mengunggah dokumen',
        allowOutsideClick: false,
        allowEscapeKey: false,
        heightAuto: false,
        backdrop: 'rgba(0,0,0,0.6)',
        didOpen: () => {
          const popup = Swal.getPopup();
          if (popup) {
            popup.style.zIndex = '99999';
          }
          const container = Swal.getContainer();
          if (container) {
            container.style.zIndex = '99998';
          }
        }
      });
      // Note: removed list-gcs check — rely on employee.documents returned by backend
    }
  };

  const copyToClipboard = (text, documentName) => {
    navigator.clipboard.writeText(text);
    setCopiedLink(documentName);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  // Helper untuk build URL view & download dari backend GCS
  const buildGcsViewUrl = (employee, documentLabel) => {
    if (!employee) return '#';
    const nama = encodeURIComponent(employee.full_name || '');
    const nim = encodeURIComponent(employee.emp_id || '');
    const sanitizedLabel = documentLabel
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const document_type = encodeURIComponent(sanitizedLabel);
    return `https://apidashboard.eurekagroup.id/documents/view-gcs?nama=${nama}&nim=${nim}&document_type=${document_type}`;
  };

  const buildGcsDownloadUrl = (employee, documentLabel) => {
    if (!employee) return '#';
    const nama = encodeURIComponent(employee.full_name || '');
    const nim = encodeURIComponent(employee.emp_id || '');
    const sanitizedLabel = documentLabel
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const document_type = encodeURIComponent(sanitizedLabel);
    return `https://apidashboard.eurekagroup.id/documents/download-gcs?nama=${nama}&nim=${nim}&document_type=${document_type}`;
  };
  // Removed list-gcs helpers: we will rely on employee.documents returned by backend

  // Open document logic (prefer public link -> direct http gsUri -> download endpoint)
  const openDocument = (employeeObj, docObj, documentLabel) => {
    const sanitizedLabel = (documentLabel || '').replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
    const publicLink = docObj && docObj.document_code ? `https://dashboard.eurekagroup.id/public/document/${docObj.document_code}` : null;
    const directUrl = docObj && docObj.gsUri && typeof docObj.gsUri === 'string' && (docObj.gsUri.startsWith('http') || docObj.gsUri.startsWith('https')) ? docObj.gsUri : null;

    const fallback = buildGcsDownloadUrl(employeeObj, sanitizedLabel);
    const url = publicLink || directUrl || fallback;
    if (url && url !== '#') {
      window.open(url, '_blank');
    } else {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Tidak ada dokumen',
        text: 'Dokumen tidak ditemukan untuk karyawan ini.'
      });
    }
  };

  const documentCategories = [
    { name: 'Formulir Biodata Pelamar Kerja', key: 'biodata_pelamar' },
    { name: 'Surat Lamaran (Wajib Tulis Tangan)', key: 'surat_lamaran' },
    { name: 'Upload CV', key: 'upload_cv' },
    { name: 'Foto Full Body (Wajib)', key: 'foto_full_body' },
    { name: 'FC KTP', key: 'fc_ktp' },
    { name: 'FC SIM (Yang Masih Berlaku)', key: 'fc_sim' },
    { name: 'Pas Foto 4X6 Berwarna Merah', key: 'pas_foto' },
    { name: 'Hasil Test MTK (Wajib)', key: 'hasil_test_mtk' },
    { name: 'FC BPJS Kesehatan', key: 'bpjs_kesehatan' },
    { name: 'FC BPJS Ketenagakerjaan', key: 'bpjs_ketenagakerjaan' },
    { name: 'Sertifikat Vaksin 1 & 2', key: 'vaksin' },
    { name: 'FC NPWP', key: 'fc_npwp' },
    { name: 'FC Rekening Mandiri (Wajib)', key: 'fc_rekening_mandiri' },
    { name: 'FC Akte Lahir', key: 'fc_akte_lahir' },
    { name: 'FC Akte Nikah (Jika Sudah Menikah)', key: 'fc_akte_nikah' },
    { name: 'Surat Kesehatan (Wajib)', key: 'surat_kesehatan' },
    { name: 'FC Ijazah (Wajib)', key: 'fc_ijazah' },
    { name: 'Transkrip Nilai (Wajib)', key: 'transkrip_nilai' },
    { name: 'FC Sertifikat (Jika Memiliki)', key: 'fc_sertifikat' },
    { name: 'Surat Keterangan Kerja (Jika Memiliki)', key: 'surat_kerja' },
    { name: 'FC SKCK (Wajib)', key: 'fc_skck' },
    { name: 'Surat Pakta Integritas', key: 'surat_keterangan' },
    { name: 'Surat Jaminan', key: 'surat_jaminan' },
    { name: 'FC KTP Ayah', key: 'fc_ktp_ayah' },
    { name: 'FC Kartu Keluarga', key: 'fc_kartu_keluarga' },
    { name: 'FC KTP Ayah/Ibu/Penjamin (Wajib)', key: 'fc_ktp_ayah_ibu_penjamin' },
    { name: 'Hasil Tes DISC', key: 'disc_test' },
    { name: 'Dan Lain Lain', key: 'dan_lain_lain' }
  ];

  const filteredData = data
    .filter(employee =>
      employee.full_name && employee.full_name.toLowerCase().includes(searchText.toLowerCase()) &&
      (selectedBU ? employee.id_bu?._id === selectedBU : true) &&
      (selectedOffice ? employee.id_office?._id === selectedOffice : true) &&
      // filter by tab (Active / Inactive)
      (tab === 'Active' ? employee.is_active === 'Active' : employee.is_active !== 'Active')
    )
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));

  return (
    <div className="animate-fade-in">
      <StatusConfirmationModal
        open={statusModal.open}
        onClose={() => setStatusModal({ open: false, data: null })}
        onConfirm={handleStatusConfirm}
        title="Update Employee Status"
        message="Are you sure you want to change the status for this employee?"
        currentStatus={statusModal.data?.currentStatus || ''}
        newStatus={statusModal.data?.newStatus || ''}
      />
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Karyawan
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data karyawan dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/kontak/employee/add')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Karyawan
            </Button>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          {/* Filter Section */}
          <Box sx={{
            mb: 3,
            p: 3,
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Cari Karyawan"
                  variant="outlined"
                  value={searchText}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={selectedBU}
                    onChange={handleBUChange}
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterIcon color="action" />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Unit Bisnis</em>
                    </MenuItem>
                    {businessUnits.map(bu => (
                      <MenuItem key={bu._id} value={bu._id}>
                        {bu.bu_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={selectedOffice}
                    onChange={handleOfficeChange}
                    displayEmpty
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Kantor</em>
                    </MenuItem>
                    {offices.map(office => (
                      <MenuItem key={office._id} value={office._id}>
                        {office.office_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ width: '100%' }}>
                  <Tabs
                    value={tab}
                    onChange={(e, value) => setTab(value)}
                    variant="fullWidth"
                    textColor="primary"
                    indicatorColor="primary"
                    aria-label="active-inactive-tabs"
                  >
                    <Tab label={`Active (${data.filter(d => d.is_active === 'Active').length})`} value="Active" />
                    <Tab label={`Inactive (${data.filter(d => d.is_active !== 'Active').length})`} value="Inactive" />
                  </Tabs>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {filteredData.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Karyawan
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table Section */}
          <Box sx={{
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            {loading ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '40vh',
                gap: 3
              }}>
                <Box sx={{ position: 'relative' }}>
                  <CircularProgress
                    size={60}
                    thickness={4}
                    sx={{
                      color: 'primary.main',
                      animationDuration: '550ms'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <PersonIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                    Memuat Data Karyawan
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Mohon tunggu sebentar...
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          No
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          minWidth: 200
                        }}>
                          Karyawan
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Dokumen
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Jenis Kelamin
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Telepon
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Departemen
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Unit Bisnis
                        </TableCell>
                        <TableCell sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textAlign: 'center'
                        }}>
                          Aksi
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((employee, index) => (
                          <TableRow
                            key={employee._id}
                            sx={{
                              '&:hover': {
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                                transform: 'scale(1.001)',
                                transition: 'all 0.2s ease'
                              },
                              '&:nth-of-type(even)': {
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                              }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>
                              {index + 1 + page * rowsPerPage}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                  sx={{ width: 40, height: 40 }}
                                >
                                  {employee.full_name?.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DotIcon
                                      sx={{
                                        color: employee.is_active === 'Active' ? 'success.main' : 'error.main',
                                        fontSize: 12
                                      }}
                                    />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      {employee.full_name}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                    {employee.email}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                    ID: {employee.emp_id}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Klik untuk kelola dokumen" arrow>
                                <Chip
                                  label={`${employee.document_count || 0} / 22`}
                                  variant="outlined"
                                  color={
                                    (employee.document_count || 0) >= 18 ? "success" :
                                      (employee.document_count || 0) >= 10 ? "warning" : "error"
                                  }
                                  size="small"
                                  onClick={() => handleDocumentClick(employee)}
                                  sx={{
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      transform: 'scale(1.1)',
                                      boxShadow: 2
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                  icon={<DocumentIcon />}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {employee.sex === 'Male' ? '♂️ Laki-laki' : employee.sex === 'Female' ? '♀️ Perempuan' : employee.sex}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                0{employee.mobile_phone}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={employee.id_department?.department_name || '-'}
                                variant="outlined"
                                color="secondary"
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={employee.id_bu?.bu_name || (employee.staticData?.workInfo?.employee_group || '-')}
                                variant="outlined"
                                color="info"
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <Tooltip title="Lihat & Upload Dokumen" arrow>
                                  <IconButton
                                    onClick={() => handleDocumentClick(employee)}
                                    sx={{
                                      color: 'warning.main',
                                      '&:hover': {
                                        bgcolor: 'warning.light',
                                        color: 'white',
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s ease'
                                    }}
                                    size="small"
                                  >
                                    <DocumentIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  onClick={() => navigate(`/kontak/employee/ViewEmployee/${employee._id}`)}
                                  sx={{
                                    color: 'success.main',
                                    '&:hover': {
                                      bgcolor: 'success.light',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                  size="small"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() => navigate(`/kontak/employee/EditEmployee/${employee._id}`)}
                                  sx={{
                                    color: 'info.main',
                                    '&:hover': {
                                      bgcolor: 'info.light',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                  size="small"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDeleteClick(employee._id)}
                                  sx={{
                                    color: 'error.main',
                                    '&:hover': {
                                      bgcolor: 'error.light',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                  size="small"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                                <FormControl size="small" sx={{ minWidth: 120, ml: 1 }}>
                                  <Select
                                    value={employee.is_active || ''}
                                    onChange={(e) => handleStatusClick(employee._id, e.target.value)}
                                    size="small"
                                    sx={{ height: 30 }}
                                    renderValue={(selected) => (
                                      <Typography variant="body2" sx={{ color: selected === 'Active' ? 'success.main' : 'text.secondary', fontWeight: 600 }}>
                                        {selected === 'Active' ? 'Active' : 'Inactive'}
                                      </Typography>
                                    )}
                                  >
                                    {activeStatuses.map(status => (
                                      <MenuItem key={status} value={status}>
                                        <Typography sx={{ color: status === 'Active' ? 'success.main' : 'text.secondary' }}>
                                          {status === 'Active' ? 'Active' : 'Inactive'}
                                        </Typography>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                  }}
                />
              </>
            )}
          </Box>
        </CardContent>
        <Snackbar
          open={successPopup}
          autoHideDuration={6000}
          onClose={() => setSuccessPopup(false)}
          message={message}
        />

        {/* Document Management Modal */}
        <Dialog
          open={documentModal.open}
          onClose={handleCloseDocumentModal}
          maxWidth="md"
          fullWidth
          TransitionComponent={Zoom}
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: '#2886df',
              position: 'relative',
              overflow: 'visible'
            }
          }}
        >
          {/* City Skyline SVG Decoration */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '200px',
            opacity: 0.1,
            pointerEvents: 'none',
            zIndex: 0
          }}>
            <svg viewBox="0 0 1200 200" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
              {/* Buildings */}
              <rect x="50" y="80" width="80" height="120" fill="#fff" opacity="0.3" />
              <rect x="150" y="60" width="100" height="140" fill="#fff" opacity="0.4" />
              <rect x="270" y="90" width="70" height="110" fill="#fff" opacity="0.3" />
              <rect x="360" y="50" width="90" height="150" fill="#fff" opacity="0.5" />
              <rect x="470" y="70" width="80" height="130" fill="#fff" opacity="0.3" />
              <rect x="570" y="40" width="110" height="160" fill="#fff" opacity="0.4" />
              <rect x="700" y="85" width="75" height="115" fill="#fff" opacity="0.3" />
              <rect x="795" y="65" width="95" height="135" fill="#fff" opacity="0.4" />
              <rect x="910" y="75" width="85" height="125" fill="#fff" opacity="0.3" />
              <rect x="1015" y="55" width="100" height="145" fill="#fff" opacity="0.5" />
              {/* Windows */}
              <rect x="60" y="90" width="10" height="10" fill="#667eea" opacity="0.6" />
              <rect x="75" y="90" width="10" height="10" fill="#667eea" opacity="0.6" />
              <rect x="60" y="105" width="10" height="10" fill="#667eea" opacity="0.6" />
              <rect x="75" y="105" width="10" height="10" fill="#667eea" opacity="0.6" />
              {/* More windows scattered across buildings */}
              <rect x="170" y="75" width="12" height="12" fill="#667eea" opacity="0.6" />
              <rect x="190" y="75" width="12" height="12" fill="#667eea" opacity="0.6" />
              <rect x="380" y="65" width="12" height="12" fill="#667eea" opacity="0.6" />
              <rect x="590" y="55" width="15" height="15" fill="#667eea" opacity="0.6" />
            </svg>
          </Box>

          <DialogTitle sx={{
            background: 'transparent',
            color: 'white',
            pb: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 50, height: 50 }}>
                  <DocumentIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    📄 Dokumen Karyawan
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, mt: 0.5 }}>
                    {documentModal.employee?.full_name} ({documentModal.employee?.emp_id})
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseDocumentModal}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.2)',
                    transform: 'rotate(90deg)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{
            bgcolor: 'white',
            position: 'relative',
            zIndex: 1,
            mt: 2,
            borderRadius: '16px 16px 0 0'
          }}>
            <Box sx={{ py: 2 }}>
              {/* Public Link Section */}
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  mb: 3,
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, position: 'relative' }}>
                  <LinkIcon sx={{ fontSize: 32 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      🔗 Link Publik Dokumen
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Bagikan link ini kepada karyawan
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(255,255,255,0.95)',
                  p: 2,
                  borderRadius: 2,
                  position: 'relative'
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      color: 'primary.main',
                      fontWeight: 600,
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      wordBreak: 'break-all'
                    }}
                  >
                    {documentModal.employee?.publicToken
                      ? `${window.location.origin}/public/documents/${documentModal.employee.publicToken}`
                      : 'Generating link...'
                    }
                  </Typography>
                  <Tooltip title={copiedLink === 'public' ? "Tersalin!" : "Salin Link"} arrow>
                    <IconButton
                      onClick={() => copyToClipboard(
                        `${window.location.origin}/public/documents/${documentModal.employee?.publicToken}`,
                        'public'
                      )}
                      disabled={!documentModal.employee?.publicToken}
                      sx={{
                        color: copiedLink === 'public' ? 'success.main' : 'primary.main',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {copiedLink === 'public' ? <CheckIcon /> : <CopyIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.9 }}>
                  ⏱️ Link ini berlaku selama 72 jam dari sekarang
                </Typography>
                {documentModal.employee?.tokenExpiry && (
                  <Typography variant="body2" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                    Sisa waktu: {tokenRemaining || 'menghitung...'}
                  </Typography>
                )}
              </Paper>

              <Dialog
                open={customUploadModal.open}
                onClose={handleCloseCustomUploadModal}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Zoom}
                PaperProps={{ sx: { borderRadius: 3, background: 'white', position: 'relative', overflow: 'visible' } }}
              >
                <DialogTitle sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 50, height: 50 }}>
                      <UploadIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                        Upload Dokumen Custom
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, mt: 0.5 }}>
                        {customUploadModal.employee?.full_name} ({customUploadModal.employee?.emp_id})
                      </Typography>
                    </Box>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ bgcolor: 'white', position: 'relative', zIndex: 1, mt: 2, borderRadius: '16px 16px 0 0' }}>
                  <Box sx={{ py: 2 }}>
                    {customUploadModal.files.map((f, idx) => (
                      <Paper key={idx} elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Filename (bebas, wajib diisi)"
                              variant="outlined"
                              fullWidth
                              value={f.filename}
                              onChange={e => handleCustomFileChange(idx, 'filename', e.target.value)}
                              size="small"
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<UploadIcon />}
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                              fullWidth
                              disabled={customUploadModal.uploading}
                            >
                              {f.file ? f.file.name : 'Pilih File'}
                              <input
                                type="file"
                                accept="image/*,.pdf"
                                hidden
                                onChange={e => handleCustomFileChange(idx, 'file', e.target.files[0])}
                              />
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AddIcon />}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, mb: 2 }}
                      onClick={handleAddCustomFileField}
                      disabled={customUploadModal.uploading}
                    >
                      Tambah File
                    </Button>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ bgcolor: 'white', p: 3, borderTop: '1px solid', borderColor: 'grey.200' }}>
                  <Button
                    onClick={handleCloseCustomUploadModal}
                    variant="outlined"
                    sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600, mr: 2 }}
                    disabled={customUploadModal.uploading}
                  >
                    Batal
                  </Button>
                  <Button
                    onClick={handleCustomUpload}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600 }}
                    disabled={customUploadModal.uploading || customUploadModal.files.some(f => !f.filename || !f.file)}
                  >
                    {customUploadModal.uploading ? <CircularProgress size={18} sx={{ color: 'white', mr: 1 }} /> : null}
                    Upload Dokumen
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Documents Grid */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>
                📋 Daftar Dokumen
              </Typography>
              <Grid container spacing={2}>
                {documentCategories.map((doc, index) => {
                  // Consider both backend document records and any files found in GCS
                  const existingDoc = documentModal.employee?.documents?.find(
                    d => d.category_name === doc.key
                  ) || (() => {
                    // fallback: look into gcsItems attached to the employee and try to match by filename
                    const gcs = documentModal.employee?.gcsItems;
                    if (!gcs || !Array.isArray(gcs) || gcs.length === 0) return null;
                    const sanitized = (doc.name || '').replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
                    const match = gcs.find(item => {
                      const filename = ((item.filename || item.name || item.document_name) || '').toString().toLowerCase();
                      return filename.includes(sanitized) || filename === sanitized;
                    });
                    if (!match) return null;
                    // return a synthetic doc-like object so UI can display view/download
                    return {
                      document_name: match.document_name || match.filename || match.name,
                      filename: match.filename || match.name || match.document_name,
                      gsUri: match.gsUri,
                      viewUrl: match.viewUrl,
                      downloadUrl: match.downloadUrl,
                      document_code: match.document_code || null
                    };
                  })();
                  const isUploaded = Boolean(existingDoc);

                  return (
                    <Grid item xs={12} sm={6} key={doc.key}>
                      <Fade in timeout={300 + index * 50}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: isUploaded ? 'success.main' : 'grey.300',
                            background: isUploaded
                              ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                              : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 6
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Avatar
                              sx={{
                                bgcolor: isUploaded ? 'success.main' : 'grey.400',
                                width: 40,
                                height: 40
                              }}
                            >
                              {existingDoc ? <CheckIcon /> : <DocumentIcon />}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  mb: 1,
                                  color: existingDoc ? 'success.main' : 'text.primary'
                                }}
                              >
                                {doc.name}
                              </Typography>

                              {isUploaded ? (
                                <Box>
                                  <Chip
                                    label="✓ Sudah Upload"
                                    size="small"
                                    color="success"
                                    sx={{ mb: 1, fontWeight: 600 }}
                                  />

                                  {/* Aksi: View + Perbarui (update) */}
                                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      onClick={() => openDocument(documentModal.employee, existingDoc, doc.name)}
                                      sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.8rem', px: 1.5 }}
                                    >
                                      View
                                    </Button>

                                    {/* hidden input for admin update */}
                                    <input
                                      accept="image/*,.pdf"
                                      style={{ display: 'none' }}
                                      id={`upload-admin-${doc.key}`}
                                      type="file"
                                      onChange={(e) => {
                                        if (e.target.files[0]) {
                                          handleFileUpload(doc.name, e.target.files[0]);
                                        }
                                      }}
                                    />
                                    <label htmlFor={`upload-admin-${doc.key}`} style={{ width: '100%' }}>
                                      <Button
                                        component="span"
                                        variant="outlined"
                                        size="small"
                                        startIcon={<UploadIcon />}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, width: '100%' }}
                                      >
                                        Perbarui Doc
                                      </Button>
                                    </label>
                                  </Box>

                                  {/* Tetap tampilkan kode dokumen & link publik lama (jika masih dipakai) */}
                                  {/* show public code + copy only when document_code exists */}
                                  {existingDoc && existingDoc.document_code && (
                                    <Box sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      mt: 1,
                                      p: 1,
                                      bgcolor: 'rgba(255,255,255,0.7)',
                                      borderRadius: 1
                                    }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          flex: 1,
                                          fontFamily: 'monospace',
                                          fontSize: '0.7rem',
                                          color: 'text.secondary'
                                        }}
                                      >
                                        {`.../${existingDoc.document_code}`}
                                      </Typography>
                                      <Tooltip title={copiedLink === doc.key ? "Tersalin!" : "Salin Link"} arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => copyToClipboard(
                                            `https://dashboard.eurekagroup.id/public/document/${existingDoc.document_code}`,
                                            doc.key
                                          )}
                                          sx={{
                                            color: copiedLink === doc.key ? 'success.main' : 'primary.main'
                                          }}
                                        >
                                          {copiedLink === doc.key ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  )}
                                </Box>
                              ) : (
                                <Box>
                                  <input
                                    accept="image/*,.pdf"
                                    style={{ display: 'none' }}
                                    id={`upload-${doc.key}`}
                                    type="file"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        handleFileUpload(doc.name, e.target.files[0]);
                                      }
                                    }}
                                  />
                                  <label htmlFor={`upload-${doc.key}`}>
                                    <Button
                                      component="span"
                                      variant="outlined"
                                      size="small"
                                      startIcon={uploadingDoc === doc.name ? <CircularProgress size={16} /> : <UploadIcon />}
                                      disabled={uploadingDoc === doc.name}
                                      sx={{
                                        mt: 1,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600
                                      }}
                                      fullWidth
                                    >
                                      {uploadingDoc === doc.name ? 'Uploading...' : 'Upload Dokumen'}
                                    </Button>
                                  </label>
                                  {doc.key === 'dan_lain_lain' && (
                                    <>
                                      <Button
                                        variant="contained"
                                        color="info"
                                        startIcon={<AddIcon />}
                                        sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                        onClick={() => handleOpenCustomUploadModal(documentModal.employee)}
                                      >
                                        Upload Dokumen Custom
                                      </Button>
                                      {/* Daftar dokumen custom yang sudah di-upload */}
                                      {documentModal.employee?.documents?.filter(d => d.category_name === 'dan_lain_lain' || d.category_name === 'lain_lain' || d.category_name === 'custom' || d.category_name === 'other').length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'info.main' }}>
                                            Dokumen Lain-Lain yang sudah di-upload:
                                          </Typography>
                                          {documentModal.employee.documents.filter(d => d.category_name === 'dan_lain_lain' || d.category_name === 'lain_lain' || d.category_name === 'custom' || d.category_name === 'other').map((d, i) => (
                                            <Paper key={i} elevation={1} sx={{ p: 1.5, mb: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                              <DocumentIcon color="info" sx={{ mr: 1 }} />
                                              <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                  {d.document_name || d.filename || 'Dokumen Tanpa Nama'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                  {d.uploaded_at ? `Di-upload: ${new Date(d.uploaded_at).toLocaleString()}` : ''}
                                                </Typography>
                                              </Box>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<VisibilityIcon />}
                                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                                onClick={() => openDocument(documentModal.employee, d, d.document_name || d.filename)}
                                              >
                                                Lihat
                                              </Button>
                                            </Paper>
                                          ))}
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      </Fade>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions sx={{
            bgcolor: 'white',
            p: 3,
            borderTop: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Button
              onClick={handleCloseDocumentModal}
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 4,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
                }
              }}
            >
              Tutup
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </div>
  );
};

export default EmployeePage;
